import { useState, useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import Button from "components/atoms/Button/Button";
import { formatPriceV2 } from "helpers/commonHelpers";
import AddQuoteItemDialog from "./AddQuoteItemDialog";
import CostTableRow from "./CostTableRow";
import { ICostItem } from "../InvoiceFormCostTypes";
import "../invoiceFormCosts.scss";

interface SubtotalItem {
  currency: string;
  total: number;
}

function CostTable({
  title,
  currentInvoice,
  costItems,
  invoiceConfig,
}: {
  title: string;
  currentInvoice: any;
  costItems: ICostItem[];
  invoiceConfig: any;
}) {
  const intl = useIntl();
  const [showAddQuoteItemDialog, setShowAddQuoteItemDialog] = useState<boolean>(false);
  const [subtotal, setSubtotal] = useState<any>([]);
  const id = useParams().id?.toUpperCase();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");

  const { data: shipmentDetailsData } = useGetShipmentDetails(id!, true, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const { control, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `costs.${title}`,
  });
  const costs = watch(`costs.${title}`);

  const filteredCostItems = costItems.filter((item) => {
    switch (title) {
      case "origin":
        return item.charge_type === 1;
      case "main_freight":
        return item.charge_type === 2;
      case "destination":
        return item.charge_type === 3;
      default:
        return false;
    }
  });

  useEffect(() => {
    calculateCost();
  }, [currentInvoiceId, costs]);

  const handleAddItemDialog = () => {
    setShowAddQuoteItemDialog(true);
  };

  const { invoice_type } = currentInvoice || {};

  const renderTitle = () => {
    if (title === "main_freight")
      return intl.formatMessage({ id: "quotation-form.freightCharges", defaultMessage: "Freight Charges" });
    if (title === "destination")
      return intl.formatMessage({ id: "quotation-form.destinationCharges", defaultMessage: "Destination Charges" });
    if (title === "origin")
      return intl.formatMessage({ id: "quotation-form.originCharges", defaultMessage: "Origin Charges" });
    if (title === "other") return intl.formatMessage({ id: "quotation-form.otherCharges", defaultMessage: "Other Charges" });
  };

  const handleAddAdditionalCostItem = () => {
    const additionalItem = {
      charge_name: "",
      type: 1,
      invoiced_cost: null,
      invoice_cost_currency_code: shipmentDetailsData?.financial?.invoices?.total_invoiced_amount_currency ?? "",
    };
    append(additionalItem);
  };

  const calculateCost = () => {
    const currencyTotals: { [currency: string]: SubtotalItem } = {};

    costs?.forEach((item: any) => {
      if (!item.invoice_cost_currency_code) return;
      const cost = Number(item.invoiced_cost);
      if (!isNaN(cost)) {
        if (!currencyTotals[item.invoice_cost_currency_code]) {
          currencyTotals[item.invoice_cost_currency_code] = {
            currency: item.invoice_cost_currency_code,
            total: 0,
          };
        }
        if (item.type === 2) {
          currencyTotals[item.invoice_cost_currency_code].total -= cost;
        } else {
          currencyTotals[item.invoice_cost_currency_code].total += cost;
        }
      }
    });

    const subtotalArray: SubtotalItem[] = Object.values(currencyTotals);
    setSubtotal(subtotalArray);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    calculateCost();
  };

  return (
    <div className="mb-3 pb-3" onBlur={calculateCost}>
      <AddQuoteItemDialog
        costItems={filteredCostItems}
        show={showAddQuoteItemDialog}
        close={() => setShowAddQuoteItemDialog(false)}
        append={append}
        data={fields}
        title={title}
      />
      <h6 className="mb-3">{renderTitle()}</h6>
      <table className="cost-table">
        <thead>
          <tr>
            <th>
              <FormattedMessage id="quotation-form.chargeName" defaultMessage="Charge name" />
            </th>
            <th>
              <FormattedMessage id="invoice.quotedCost" defaultMessage="Quoted cost" />
            </th>
            <th>
              <FormattedMessage id="new-request.packing-list.specification" defaultMessage="Specification" />
            </th>
            <th>
              <FormattedMessage id="invoice.invoicedCost" defaultMessage="Invoiced cost" />
            </th>
            <th>
              <FormattedMessage id="general.currency" defaultMessage="Currency" />
            </th>
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <th />
          </tr>
        </thead>
        <tbody>
          {fields.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center p-5">
                <FormattedMessage id="analytics.noData" defaultMessage="No data available" />
              </td>
            </tr>
          )}
          {fields.length !== 0 &&
            fields.map((data, index) => (
              <CostTableRow
                key={data.id}
                onDelete={handleDeleteItem}
                title={title}
                data={data}
                costIndex={index}
                calculateCost={calculateCost}
                invoiceConfig={invoiceConfig}
                currentInvoice={currentInvoice}
              />
            ))}
        </tbody>
      </table>
      <div className="d-flex mt-3 justify-content-between">
        <div className="d-flex" style={{ maxHeight: "22px" }}>
          <Button className="btn btn-secondary mr-3" onClick={handleAddAdditionalCostItem}>
            <FormattedMessage id="invoice.addAdditionalCostItem" defaultMessage="Add additional cost item" />
          </Button>
          {invoice_type !== "primary_invoice" && filteredCostItems.length > 0 && (
            <Button className="btn btn-secondary" onClick={handleAddItemDialog}>
              <FormattedMessage id="invoice.addItemFromQuote" defaultMessage="Add item from quote" />
            </Button>
          )}
        </div>
        <div className="d-flex">
          <FormattedMessage id="quotation-form.subtotal" defaultMessage="Subtotal" />:{" "}
          <div className="font-weight-bold ml-3">
            {subtotal.length > 0 &&
              subtotal.map((item: { total: number; currency: string }, index: number) => (
                <div key={index}>
                  {formatPriceV2(item.total)} {item.currency}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CostTable;
